import { api } from '../api.ts';
import { compact, html, mount, raw } from '../html.ts';
import { formatDate, IMPACT, options, PROBABILITY, RISK_CATEGORY, RISK_RESPONSE, RISK_STATUS, t } from '../i18n.ts';
import { canWrite, lookups } from '../state.ts';
import { openCommentThread, renderComments } from './comments.ts';
import { badge, confirmDialog, emptyState, field, openFormDialog, optionList, riskTone, select, statusBadge, textarea, toast, toastError } from '../ui.ts';

/**
 * Risk register with a 5×5 probability × impact heat matrix. The matrix is an
 * app-local prototype of a Purple UI "matrix" data visualization: a real
 * table with row/column headers, counts as text and a selectable cell filter.
 */

interface Risk {
  id: string;
  title: string;
  description: string | null;
  category: string;
  probability: number;
  impact: number;
  score: number;
  status: string;
  response: string | null;
  mitigation: string | null;
  owner_contact_id: string | null;
  owner_user_id: string | null;
  review_on: string | null;
}

const ACTIVE = new Set(['open', 'mitigating']);

async function riskFields(risk: Partial<Risk> = {}) {
  const [members, contacts] = await Promise.all([lookups.members(), lookups.contacts()]);
  return html`
    ${field(t('Titel'), 'title', 'text', { required: true, value: risk.title, span: true })}
    ${select(t('Kategorie'), 'category', options(RISK_CATEGORY, risk.category ?? 'project'))}
    ${select(t('Status'), 'status', options(RISK_STATUS, risk.status ?? 'open'))}
    ${select(t('Wahrscheinlichkeit'), 'probability', options(Object.fromEntries(Object.entries(PROBABILITY).map(([k, v]) => [k, `${k} · ${v}`])), risk.probability ?? 3), { required: true })}
    ${select(t('Auswirkung'), 'impact', options(Object.fromEntries(Object.entries(IMPACT).map(([k, v]) => [k, `${k} · ${v}`])), risk.impact ?? 3), { required: true })}
    ${select(t('Strategie'), 'response', options(RISK_RESPONSE, risk.response, t('Noch offen')))}
    ${field(t('Nächste Prüfung'), 'review_on', 'date', { value: risk.review_on })}
    ${select(t('Verantwortlich (Team)'), 'owner_user_id', optionList(members, risk.owner_user_id, t('Niemand')))}
    ${select(t('Verantwortlich (Kontakt)'), 'owner_contact_id', optionList(contacts, risk.owner_contact_id, t('Niemand')))}
    ${textarea(t('Beschreibung'), 'description', { value: risk.description, rows: 3 })}
    ${textarea(t('Maßnahmen'), 'mitigation', { value: risk.mitigation, rows: 3 })}
  `;
}

function matrix(risks: Risk[], selected: string | null) {
  const active = risks.filter((r) => ACTIVE.has(r.status));
  const rows = [5, 4, 3, 2, 1];
  const cols = [1, 2, 3, 4, 5];
  return html`<figure class="p-visualization-frame pma-matrix-frame">
    <figcaption class="p-visualization-frame__heading"><strong>${t('Risikomatrix')}</strong><span class="pma-muted pma-small">${t('Offene und in Behandlung befindliche Risiken · Zelle wählen zum Filtern')}</span></figcaption>
    <table class="pma-matrix">
      <caption class="p-visually-hidden">${t('Anzahl Risiken nach Wahrscheinlichkeit (Zeilen) und Auswirkung (Spalten)')}</caption>
      <thead><tr><td></td>${cols.map((c) => html`<th scope="col" title="${IMPACT[c]}">${c}</th>`)}</tr></thead>
      <tbody>${rows.map(
        (p) => html`<tr><th scope="row" title="${PROBABILITY[p]}">${p}</th>${cols.map((i) => {
          const count = active.filter((r) => r.probability === p && r.impact === i).length;
          const key = `${p}:${i}`;
          return html`<td class="pma-matrix__cell pma-matrix__cell--${riskTone(p * i)}">
            <button type="button" data-cell="${key}" aria-pressed="${selected === key ? 'true' : 'false'}"
              aria-label="${t('Wahrscheinlichkeit {p}, Auswirkung {i}: {n} Risiken', { p, i, n: count })}">${count || ''}</button></td>`;
        })}</tr>`,
      )}</tbody>
    </table>
    <p class="pma-matrix__axes pma-muted pma-small"><span>${t('↑ Wahrscheinlichkeit')}</span><span>${t('Auswirkung →')}</span></p>
  </figure>`;
}

export async function renderRisks(target: HTMLElement, projectId: string) {
  let risks: Risk[] = [];
  let cell: string | null = null;
  let showClosed = false;
  const [members, contacts] = await Promise.all([lookups.members(), lookups.contacts()]);
  const people = new Map([...members, ...contacts].map((p) => [p.id, p.label]));

  async function load() {
    risks = (await api.get<{ items: Risk[] }>(`/projects/${projectId}/risks?limit=500`)).items;
    draw();
  }

  function draw() {
    let list = risks.filter((r) => showClosed || ACTIVE.has(r.status));
    if (cell) {
      const [p, i] = cell.split(':').map(Number);
      list = list.filter((r) => r.probability === p && r.impact === i);
    }
    mount(
      target,
      html`<div class="pma-risk-layout">
        ${matrix(risks, cell)}
        <section class="pma-risk-register">
          <div class="p-table-toolbar pma-toolbar">
            <label class="p-check"><input type="checkbox" data-closed${showClosed ? raw(' checked') : ''}> <span>${t('Geschlossene zeigen')}</span></label>
            ${cell ? html`<button type="button" class="p-button p-button--ghost p-button--sm" data-clear>${t('Filter {cell} aufheben', { cell: cell.replace(':', ' × ') })}</button>` : ''}
            <div class="p-table-toolbar__actions">${canWrite() ? html`<button class="p-button p-button--primary" type="button" data-new>${t('Neues Risiko')}</button>` : ''}</div>
          </div>
          ${
            list.length
              ? html`<ul class="pma-list">${list.map(
                  (r) => html`<li class="pma-list__row">
                    <div><button type="button" class="p-button p-button--link pma-left" data-risk="${r.id}">${r.title}</button>
                      <div class="pma-muted pma-small">${RISK_CATEGORY[r.category]} · ${PROBABILITY[r.probability]} × ${IMPACT[r.impact]}
                        ${r.owner_user_id || r.owner_contact_id ? html` · ${people.get((r.owner_user_id ?? r.owner_contact_id)!)}` : ''}
                        ${r.review_on ? html` · ${t('Prüfung {date}', { date: formatDate(r.review_on) })}` : ''}</div>
                      ${r.mitigation ? html`<div class="pma-small">${t('Maßnahme')}: ${r.mitigation}</div>` : ''}</div>
                    <div class="pma-list__meta">${statusBadge(r.status, RISK_STATUS)} ${badge(t('Score {n}', { n: r.score }), riskTone(r.score))}</div>
                  </li>`,
                )}</ul>`
              : emptyState(risks.length ? t('Keine Treffer') : t('Keine Risiken erfasst'), risks.length ? t('Filter anpassen, um weitere Risiken zu sehen.') : t('Bewerte Wahrscheinlichkeit und Auswirkung von 1 bis 5 – der Score entsteht automatisch.'))
          }
        </section>
      </div>`,
    );
  }

  async function edit(risk: Risk | null) {
    const dialog = openFormDialog({
      title: risk ? t('Risiko bearbeiten') : t('Neues Risiko'),
      wide: true,
      fields: await riskFields(risk ?? {}),
      submitLabel: risk ? t('Speichern') : t('Anlegen'),
      onSubmit: async (data) => {
        if (risk) await api.patch(`/risks/${risk.id}`, data);
        else await api.post(`/projects/${projectId}/risks`, compact(data));
        toast(risk ? t('Risiko gespeichert') : t('Risiko erfasst'));
        await load();
      },
    });
    if (risk) {
      const thread = document.createElement('section');
      thread.className = 'pma-span-2';
      dialog.querySelector('.pma-form-grid')!.append(thread);
      void renderComments(thread, 'risk', risk.id);
    }
    if (risk && canWrite()) {
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'p-button p-button--ghost pma-danger-text';
      remove.textContent = t('Löschen');
      remove.addEventListener('click', async () => {
        dialog.close();
        if (await confirmDialog(t('Risiko löschen?'), t('„{title}“ wird endgültig gelöscht.', { title: risk.title }))) {
          await api.delete(`/risks/${risk.id}`).catch(toastError);
          await load();
        }
      });
      dialog.querySelector('.p-dialog__actions')!.prepend(remove);
    }
  }

  target.addEventListener('click', (event) => {
    const element = event.target as HTMLElement;
    const cellButton = element.closest<HTMLElement>('[data-cell]');
    if (cellButton) {
      cell = cell === cellButton.dataset.cell ? null : cellButton.dataset.cell!;
      draw();
      target.querySelector<HTMLElement>(`[data-cell="${cellButton.dataset.cell}"]`)?.focus();
      return;
    }
    if (element.closest('[data-clear]')) {
      cell = null;
      draw();
    }
    if (element.closest('[data-new]')) void edit(null);
    const id = element.closest<HTMLElement>('[data-risk]')?.dataset.risk;
    const risk = risks.find((r) => r.id === id);
    if (risk) canWrite() ? void edit(risk) : openCommentThread('risk', risk.id, risk.title);
  });
  target.addEventListener('change', (event) => {
    if ((event.target as HTMLElement).matches('[data-closed]')) {
      showClosed = (event.target as HTMLInputElement).checked;
      draw();
    }
  });

  await load();
}
